import { useState, useCallback, useEffect } from 'react';
import { Badge, Card, CardHeader, Button, Input, Select, Modal, LoadingSkeleton, Pagination } from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  useJournalEntries,
  useCreateEntry,
  useChartOfAccounts,
  useAccountingCtx,
  type JournalEntry,
  type JournalEntryInput,
  type Account,
} from './useAccounting';

/* ── Journal Line Form Row ── */

interface LineFormRow {
  accountId: string;
  debit: string;
  credit: string;
}

function LineRow({
  line,
  index,
  accounts,
  onChange,
  onRemove,
  canRemove,
}: {
  line: LineFormRow;
  index: number;
  accounts: { value: string; label: string }[];
  onChange: (index: number, field: keyof LineFormRow, value: string) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
      <div style={{ flex: 1, minWidth: '180px' }}>
        <Select
          label={index === 0 ? 'Conta' : undefined}
          value={line.accountId}
          onChange={(e) => onChange(index, 'accountId', e.target.value)}
        >
          <option value="">Selecione...</option>
          {accounts.map((a) => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </Select>
      </div>
      <div style={{ flex: '0 0 120px' }}>
        <Input
          label={index === 0 ? 'Débito' : undefined}
          type="number"
          step="0.01"
          min="0"
          value={line.debit}
          onChange={(e) => onChange(index, 'debit', e.target.value)}
        />
      </div>
      <div style={{ flex: '0 0 120px' }}>
        <Input
          label={index === 0 ? 'Crédito' : undefined}
          type="number"
          step="0.01"
          min="0"
          value={line.credit}
          onChange={(e) => onChange(index, 'credit', e.target.value)}
        />
      </div>
      {canRemove && (
        <Button variant="ghost" size="sm" onClick={() => onRemove(index)} style={{ marginBottom: '2px', color: '#ba1a1a' }}>
          X
        </Button>
      )}
    </div>
  );
}

/* ── Entry Form Modal ── */

function JournalEntryFormModal({
  isOpen,
  onClose,
  accounts,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  accounts: Account[];
  onSubmit: (input: JournalEntryInput) => Promise<void>;
}): JSX.Element {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<LineFormRow[]>([{ accountId: '', debit: '', credit: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setDate(new Date().toISOString().split('T')[0]);
      setDescription('');
      setLines([{ accountId: '', debit: '', credit: '' }]);
      setError(null);
    }
  }, [isOpen]);

  const handleLineChange = useCallback((index: number, field: keyof LineFormRow, value: string) => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, [field]: value } : l)));
  }, []);

  const handleAddLine = useCallback(() => {
    setLines((prev) => [...prev, { accountId: '', debit: '', credit: '' }]);
  }, []);

  const handleRemoveLine = useCallback((index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /* Flatten accounts for dropdown */
  function flattenAccounts(list: Account[]): { value: string; label: string }[] {
    const result: { value: string; label: string }[] = [];
    for (const a of list) {
      result.push({ value: a.id, label: `${a.code} - ${a.name}` });
      if (a.children) result.push(...flattenAccounts(a.children));
    }
    return result;
  }

  const accountOptions = flattenAccounts(accounts);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!description.trim()) { setError('Descrição e obrigatória.'); return; }
    if (lines.length < 2) { setError('O lancamento deve ter pelo menos 2 linhas.'); return; }

    const parsedLines = lines.map((l) => ({
      accountId: l.accountId,
      debit: parseFloat(l.debit) || 0,
      credit: parseFloat(l.credit) || 0,
    }));

    const hasEmptyAccount = parsedLines.some((l) => !l.accountId);
    if (hasEmptyAccount) { setError('Todas as linhas devem ter uma conta selecionada.'); return; }

    const totalDebit = parsedLines.reduce((s, l) => s + l.debit, 0);
    const totalCredit = parsedLines.reduce((s, l) => s + l.credit, 0);

    if (totalDebit === 0 && totalCredit === 0) {
      setError('Pelo menos uma linha deve ter valor de debito ou credito.');
      return;
    }

    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      setError(`Total de debitos (${formatCurrency(totalDebit)}) deve ser igual ao total de creditos (${formatCurrency(totalCredit)}).`);
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit({ date: date ?? '', description: (description ?? '').trim(), lines: parsedLines });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar lancamento.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Novo Lançamento" size="lg">
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <div style={{ flex: '0 0 200px' }}>
              <Input label="Data" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
            </div>
          </div>
          <Input label="Descrição" value={description} onChange={(e) => setDescription(e.target.value)} required placeholder="Ex: Venda a vista" />

          <div style={{ borderTop: '1px solid #e0e3e5', paddingTop: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#191c1e' }}>Linhas do Lançamento</span>
              <Button variant="outline" size="sm" onClick={handleAddLine}>+ Adicionar linha</Button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              {lines.map((line, i) => (
                <LineRow
                  key={i}
                  line={line}
                  index={i}
                  accounts={accountOptions}
                  onChange={handleLineChange}
                  onRemove={handleRemoveLine}
                  canRemove={lines.length > 1}
                />
              ))}
            </div>

            {lines.length >= 2 && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.8125rem', color: '#43474e', textAlign: 'right' }}>
                Total Debito: {formatCurrency(lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0))} |
                Total Credito: {formatCurrency(lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0))}
              </div>
            )}
          </div>

          {error && <p role="alert" style={{ margin: 0, fontSize: '0.8125rem', color: '#ba1a1a' }}>{error}</p>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e0e3e5' }}>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={submitting}>Lancar</Button>
        </div>
      </form>
    </Modal>
  );
}

/* ── Journal Entry Row (expandable) ── */

const ENTRY_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Rascunho',
  POSTED: 'Lançado',
  REVERSED: 'Estornado',
  VOID: 'Anulado',
};

const ENTRY_STATUS_VARIANT: Record<string, 'success' | 'neutral' | 'warning' | 'error'> = {
  DRAFT: 'neutral',
  POSTED: 'success',
  REVERSED: 'warning',
  VOID: 'error',
};

function JournalEntryRow({
  entry,
  accountLabel,
}: {
  entry: JournalEntry;
  accountLabel: (accountId: string) => string;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  /* Totais do servidor: o lancamento e a unidade contabil e o total dele e
     gravado junto. Somar as linhas aqui daria outro numero se alguma linha
     nao viesse na resposta. */
  const difference = +(entry.totalDebit - entry.totalCredit).toFixed(2);

  return (
    <>
      <tr className="journal-row" style={{ cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <td className="je-cell je-nowrap">{formatDate(entry.date)}</td>
        <td className="je-cell je-mono">{entry.entryNumber}</td>
        <td className="je-cell">{entry.description}</td>
        <td className="je-cell je-num">{formatCurrency(entry.totalDebit)}</td>
        <td className="je-cell je-num">{formatCurrency(entry.totalCredit)}</td>
        <td className="je-cell">
          <Badge variant={ENTRY_STATUS_VARIANT[entry.status] ?? 'neutral'} size="sm">
            {ENTRY_STATUS_LABEL[entry.status] ?? entry.status}
          </Badge>
        </td>
        <td className="je-cell" style={{ textAlign: 'center' }}>
          <span
            className="material-symbols-outlined"
            style={{ fontSize: '18px', color: '#43474e' }}
            aria-hidden="true"
          >
            {expanded ? 'expand_more' : 'chevron_right'}
          </span>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="je-detail">
            <div className="je-detail__head">
              <div>
                <span className="je-detail__label">Origem</span>
                <span className="je-detail__value">
                  {entry.source}
                  {entry.sourceId ? ` · ${entry.sourceId}` : ''}
                </span>
              </div>
              <div>
                <span className="je-detail__label">Tipo</span>
                <span className="je-detail__value">{entry.type}</span>
              </div>
              <div>
                <span className="je-detail__label">Lançado por</span>
                <span className="je-detail__value">{entry.postedBy ?? '—'}</span>
              </div>
              <div>
                <span className="je-detail__label">Lançado em</span>
                <span className="je-detail__value">
                  {entry.postedAt ? formatDate(entry.postedAt.slice(0, 10)) : '—'}
                </span>
              </div>
            </div>

            <table className="je-lines">
              <thead>
                <tr>
                  <th>Conta</th>
                  <th style={{ textAlign: 'right' }}>Débito</th>
                  <th style={{ textAlign: 'right' }}>Crédito</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>D/C</th>
                </tr>
              </thead>
              <tbody>
                {entry.lines.map((line) => (
                  <tr key={line.id}>
                    <td>{accountLabel(line.accountId)}</td>
                    <td className="je-num">{line.debit > 0 ? formatCurrency(line.debit) : '—'}</td>
                    <td className="je-num">{line.credit > 0 ? formatCurrency(line.credit) : '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={line.debit > 0 ? 'je-dc je-dc--d' : 'je-dc je-dc--c'}>
                        {line.debit > 0 ? 'D' : 'C'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Totais</td>
                  <td className="je-num">{formatCurrency(entry.totalDebit)}</td>
                  <td className="je-num">{formatCurrency(entry.totalCredit)}</td>
                  <td />
                </tr>
                <tr className={difference === 0 ? 'je-diff je-diff--ok' : 'je-diff je-diff--bad'}>
                  {/* A diferenca e a conferencia que o contador faz antes de
                      aceitar o lancamento: partida dobrada fecha em zero. */}
                  <td>Diferença</td>
                  <td colSpan={2} className="je-num">{formatCurrency(difference)}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }} aria-hidden="true">
                      {difference === 0 ? 'check_circle' : 'error'}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── Page ── */

export function JournalPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');

  const ctx = useAccountingCtx();
  const { data, isLoading, error } = useJournalEntries(ctx, { page, limit: 20, startDate: startDate || undefined, endDate: endDate || undefined });

  /* A linha do lancamento traz `accountId`; codigo e nome vem do plano de
     contas. Sem isso a coluna Conta mostraria UUID — mesmo defeito ja
     corrigido em Lotes e em BOM. */
  const { data: chart } = useChartOfAccounts(ctx);
  const accountLabel = (accountId: string): string => {
    const acc = (chart ?? []).find((a) => a.id === accountId);
    return acc ? `${acc.code} - ${acc.name}` : accountId;
  };
  const { data: accounts } = useChartOfAccounts(ctx);
  const createMutation = useCreateEntry(ctx);

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToastMessage(message);
    setToastVariant(variant);
  }, []);

  const handleCreate = useCallback(
    async (input: JournalEntryInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Lançamento criado com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao criar lancamento.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [createMutation, showToast],
  );

  const handleFilter = useCallback(() => {
    setPage(1);
  }, []);

  const entries = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Page header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
          Lançamentos Contábeis
          {data && (
            <span className="font-body-md text-body-md text-on-surface-variant font-normal ml-sm">
              ({data.total} lancamentos)
            </span>
          )}
        </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Lançamentos contábeis e partidas dobradas.</p>
        </div>
        <Button variant="primary" onClick={() => setModalOpen(true)}>+ Novo Lançamento</Button>
      </div>

      {/* Filters */}
      <Card padding="1rem" variant="outlined" className="filter-card">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '0 0 180px' }}>
            <Input label="Data inicio" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div style={{ flex: '0 0 180px' }}>
            <Input label="Data fim" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <Button variant="secondary" size="sm" onClick={handleFilter}>Filtrar</Button>
          {(startDate || endDate) && (
            <Button variant="ghost" size="sm" onClick={() => { setStartDate(''); setEndDate(''); setPage(1); }}>Limpar</Button>
          )}
        </div>
      </Card>

      {/* Content */}
      <Card variant="outlined" padding="0">
        <CardHeader
          className="px-md pt-md"
          action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {data?.total ?? 0} {(data?.total ?? 0) === 1 ? 'lançamento' : 'lançamentos'}
            </span>
          }
        >
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Lançamentos</h3>
        </CardHeader>
        {/* Loading */}
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton />
            <span style={{ fontSize: '0.875rem' }}>Carregando lancamentos...</span>
          </div>
        )}

        {/* Error */}
        {error && !isLoading && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar lancamentos</p>
            <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </p>
          </div>
        )}

        {/* Empty */}
        {!isLoading && !error && entries.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f' }}>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>Nenhum lancamento encontrado.</p>
            <Button variant="outline" size="sm" onClick={() => setModalOpen(true)}>Criar primeiro lancamento</Button>
          </div>
        )}

        {/* Table */}
        {!isLoading && !error && entries.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Data', 'Nº', 'Histórico', 'Total Débito', 'Total Crédito', 'Status', ''].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: ['Total Débito', 'Total Crédito'].includes(h) ? 'right' : 'left',
                        padding: '0.75rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <JournalEntryRow key={entry.id} entry={entry} accountLabel={accountLabel} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !error && totalPages > 1 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #eceef0' }}>
            <Pagination page={page} totalPages={totalPages} onChange={(p) => setPage(p)} />
          </div>
        )}
      </Card>

      {/* Toast */}
      {toastMessage && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            background: toastVariant === 'success' ? '#d3f5d6' : '#ffdad6',
            border: `1px solid ${toastVariant === 'success' ? '#b6ebbb' : '#ffb4ab'}`,
            color: toastVariant === 'success' ? '#2e7d32' : '#ba1a1a',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 10000,
            maxWidth: '400px',
            animation: 'toast-slide-in 0.3s ease',
          }}
        >
          {toastMessage}
          <button
            onClick={() => setToastMessage(null)}
            aria-label="Fechar"
            style={{ marginLeft: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1rem', padding: '0', lineHeight: 1 }}
          >
            &times;
          </button>
        </div>
      )}

      {/* Modal */}
      <JournalEntryFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        accounts={accounts ?? []}
        onSubmit={handleCreate}
      />

      <style>{`
        .je-cell { padding: 0.625rem 0.75rem; border-bottom: 1px solid #eceef0; font-size: 0.875rem; color: #191c1e; }
        .je-nowrap { white-space: nowrap; }
        .je-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; white-space: nowrap; }
        .je-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }

        .je-detail { padding: 0.75rem 1rem 1rem; background: #f7f9fb; border-bottom: 1px solid #eceef0; }
        .je-detail__head { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; margin-bottom: 0.75rem; }
        .je-detail__label { display: block; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; color: #43474e; }
        .je-detail__value { display: block; font-size: 0.875rem; color: #191c1e; }

        .je-lines { width: 100%; border-collapse: collapse; background: #ffffff; border: 1px solid #c4c6cf; border-radius: 4px; font-size: 0.8125rem; }
        .je-lines th { text-align: left; padding: 0.5rem 0.75rem; background: #f2f4f6; color: #43474e; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; border-bottom: 1px solid #c4c6cf; }
        .je-lines td { padding: 0.375rem 0.75rem; border-bottom: 1px solid #f2f4f6; }
        .je-lines tfoot td { font-weight: 600; border-top: 1px solid #c4c6cf; background: #f7f9fb; }
        .je-dc { display: inline-block; min-width: 20px; padding: 0 4px; border-radius: 2px; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; }
        .je-dc--d { background: #d6e3ff; color: #001b3c; }
        .je-dc--c { background: #e0e3e5; color: #191c1e; }
        .je-diff--ok td { color: #1b7f4d; }
        .je-diff--bad td { color: #ba1a1a; }

        .journal-row:hover td { background: #eceef0; }
        .filter-card { margin-bottom: 1rem; }
        @keyframes toast-slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/* Reexport: a copia local foi trocada pelo formatador unico. */
export { formatCurrency };
